import { useTranslate } from "hooks/useTranslate"
import { PropsWithChildren, ReactNode } from "react"

import { IntlKey, isIntlKey } from "@shared/intl"
import { TranslationEntry } from "@shared/translations/translationEntry"

/**
 * A type that can be used to pass translatable content to the component.
 * The value can be either a translation entry (preferred) or a translation key (discouraged).
 * Components using TranslatableText can use this type to describe their properties
 * that would be passed into the TranslatableText component.
 */
export type TranslatableProp = IntlKey | TranslationEntry

/**
 * Type for react-intl's rich text formatting approach (allowing specific HTML
 * attributes in translations)
 */
type RichTextValues = (chunks: ReactNode[]) => ReactNode

/**
 *  An internal interface to describe a translatable "t" property.
 */
interface TranslatableTProp {
  /**
   * The translation key that should be provided to the component
   * to get proper translation.
   */
  t?: TranslatableProp

  /**
   * Optional values that can be used for interpolation in the translation strings.
   */
  tValues?: Record<string, string | number | boolean | ReactNode | RichTextValues>
}

/**
 *  The public interface for the translatable text component. The interface allows for
 *  passing "t" and "children" props. The "t" prop takes a precedence over the "children"
 *  and will render a translated text found under the "t" key. If the "t" is not provided,
 *  the component will render the "children" prop.
 */
export interface TranslatableTextProps extends TranslatableTProp, PropsWithChildren {}

/**
 *  This is a simple component that is bundled with an interface that can be used
 *  to present translatable text or display children. Extending components would
 *  extend from this component interface to provide correct props types and then
 *  internally use this component to present the translatable text or display children.
 *
 *  ```ts
 *  import { TranslatableText, TranslatableTextProps } from "components/TranslatableText/TranslatableText"
 *
 *  export interface TextComponentProps extends TranslatableTextProps {
 *     color: TextColor
 *  }
 *
 *  export function TextComponent({ color, ...props }: TextComponentProps) {

 *     return <span className={color}>
 *       <TranslatableText {...props} />
 *     </span>
 *  }
 *  ```
 */
export function TranslatableText(props: TranslatableTextProps) {
  const translate = useTranslate()
  if (isTranslatableTProp(props) && props.t !== undefined) {
    return translate(props.t, props.tValues)
  }

  if (isPropsWithChildren(props) && props.children !== undefined) {
    return props.children
  }

  return null
}

// a helper function to detect if we are dealing with a translatable "t" property
function isTranslatableTProp(props: TranslatableTextProps): props is TranslatableTProp {
  return "t" in props && props.t !== undefined
}

// a helper function to detect if we are dealing with children props
function isPropsWithChildren(props: TranslatableTextProps): props is PropsWithChildren {
  return "children" in props && props.children !== undefined
}

/**
 * A helper function to detect if a prop is a translatable prop.
 */
export function isTranslatableProp(prop: unknown): prop is TranslatableProp {
  return prop instanceof TranslationEntry || (typeof prop === "string" && isIntlKey(prop))
}
